import { css } from '@emotion/react';

import { Flex, Heading, Icon } from '@toolbox/components';
import { makeStyles } from '@toolbox/style';
import { isObject } from '@toolbox/utils';
import { useAgentContext, useFlags, useLeadData } from '@widget/core';
import { CloseButton } from '../CloseButton';
import { CircularProgress } from '../ProgressBar';

import type { AgentInfo } from '@widget/core';
import type { AgentProps, QuestionheaderProps } from './types';

const useStyles = makeStyles(({ theme }) => ({
  header: css`
    position: relative;
    display: grid;
    grid-template-columns: 1fr auto;

    ${theme.media.up('sm')} {
      grid-template-columns: auto 1fr auto;
      align-items: start;
      margin-bottom: ${theme.spacing(0)};
      min-height: 80px;
    }
  `,
  arrowLeft: css`
    display: inline-block;
    position: absolute;
    left: ${theme.spacing(1)};
    top: -${theme.spacing(6)};
    border: ${theme.spacing(4)} solid transparent;
    border-bottom: ${theme.spacing(4)} solid ${theme.palette.colors.gray[200]};

    ${theme.media.up('sm')} {
      top: unset;
      left: -${theme.spacing(9)};
      bottom: ${theme.spacing(4)};
      border: ${theme.spacing(4)} solid transparent;
      border-right: ${theme.spacing(6)} solid ${theme.palette.colors.gray[200]};
    }
  `,
  managerName: css`
    font-size: ${theme.fontSizes.f1};
    line-height: ${theme.lineHeights.sm};

    ${theme.media.up('sm')} {
      font-weight: bold;
      font-size: ${theme.fontSizes.f3};
      padding-top: ${theme.spacing(2)};
    }
  `,
  cityName: css`
    font-size: 10px; //@TODO: theme variable update.
    line-height: ${theme.lineHeights.xl};
    ${theme.media.up('sm')} {
      font-size: ${theme.fontSizes.f1};
    }
  `,
  title: css`
    grid-row: 2 / span 1;
    grid-column: 1 / -1;
    display: flex;
    flex-grow: 1;
    align-items: center;
    position: relative;
    background-color: ${theme.palette.colors.gray[200]};
    color: ${theme.palette.colors.primary};
    font-size: ${theme.fontSizes.f5};
    line-height: ${theme.lineHeights.sm};
    min-height: 60px;
    padding: ${theme.spacing(3)};
    overflow-wrap: break-word;
    word-break: break-word;

    ${theme.media.up('sm')} {
      grid-row: auto;
      grid-column: auto;
      min-height: 80px;
      margin-top: 0;
      padding-left: ${theme.spacing(8)};
      padding-left: ${theme.spacing(8)};
      padding-top: ${theme.spacing(8)};
      padding-bottom: ${theme.spacing(8)};
      font-size: ${theme.fontSizes.f5};
      line-height: ${theme.lineHeights.xs};

      &:first-child {
        grid-column: 1 / span 2;
      }

      &:last-child {
        grid-column: 2 / -1;
      }

      &:only-child {
        grid-column: 1 / -1;
      }
    }
  `,
  closeBtn: css`
    position: absolute;
    display: none;

    ${theme.media.up('sm')} {
      display: block;
      right: 0;
      top: -${theme.spacing(16)};
    }

    ${theme.media.up('md')} {
      right: -${theme.spacing(20)};
    }
  `,
  iconWrapper: css`
    min-width: ${theme.spacing(16)};
    min-height: ${theme.spacing(12)};
    img {
      width: ${theme.spacing(10)};
      object-fit: cover;
      object-position: center;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      margin-bottom: ${theme.spacing(3)};

      ${theme.media.up('sm')} {
        width: 80px;
        height: 80px;
      }
    }
  `,
  iconLoading: css`
    width: ${theme.spacing(10)};
    height: ${theme.spacing(10)};
    border-radius: 50%;
    background-color: #f7f8fa;

    ${theme.media.up('sm')} {
      width: 80px;
      height: 80px;
      flex: 0 0 80px;
      margin-right: ${theme.spacing(8)};
    }
  `,
  figure: css`
    display: flex;
    align-items: center;
    margin: 0;
    ${theme.media.up('sm')} {
      flex-direction: column;
    }
  `,
  caption: css`
    color: ${theme.palette.colors.primary};
    padding-left: ${theme.spacing(2)};

    ${theme.media.up('sm')} {
      text-align: center;
    }
  `,
  progress: css`
    justify-self: end;
    margin-left: ${theme.spacing(5)};
  `,
  agentPlaceholder: css`
    display: flex;
    font-size: ${theme.spacing(10)};
    margin-bottom: ${theme.spacing(3)};
    ${theme.media.up('sm')} {
      font-size: 80px;
      margin-right: ${theme.spacing(8)};
    }
  `,
}));

export function QuestionHeader({
  closeButtonText,
  title,
  showProgressBar,
}: QuestionheaderProps): JSX.Element {
  const classes = useStyles();
  const { agent, isLoading } = useAgentContext();
  const agentImage = !isLoading && (isAgent(agent) ? agent : null);
  const { loadingLead, leadData, leadError } = useLeadData();
  const isProgressBarVisible = !loadingLead && !leadData?.id && !leadError && showProgressBar;

  return (
    <div css={classes.header as any}>
      {agentImage ? (
        <AgentImage agent={agentImage} />
      ) : isLoading ? (
        <div css={classes.iconLoading as any} />
      ) : (
        <div css={classes.agentPlaceholder as any}>
          <Icon type={'AgentPlaceholder'} size={'inherit'} />
        </div>
      )}
      <Heading variant="h1" as="h1" css={classes.title as any}>
        <span css={classes.arrowLeft as any} />
        {title}
      </Heading>
      {isProgressBarVisible && (
        <div css={classes.progress as any}>
          <CircularProgress />
        </div>
      )}
      {closeButtonText && <CloseButton css={classes.closeBtn as any} text={closeButtonText} />}
    </div>
  );
}

function AgentImage({ agent }: AgentProps): JSX.Element | null {
  const classes = useStyles();
  const { agentCityPrefix } = useFlags();

  if (isAgent(agent)) {
    return (
      <div css={classes.iconWrapper as any}>
        <figure css={classes.figure as any}>
          <img src={agent?.imageUrl} alt={agent?.name} />
          <Flex as="figcaption" direction="column" css={classes.caption as any}>
            <span css={classes.managerName as any}>{agent?.name}</span>
            <span css={classes.cityName as any}>{`${agentCityPrefix || ''} ${agent?.city}`}</span>
          </Flex>
        </figure>
      </div>
    );
  }
  if (typeof agent === 'string') <div css={classes.iconWrapper as any}>{agent}</div>;
  return <div css={classes.iconWrapper as any} />;
}

function isAgent(x: unknown): x is AgentInfo {
  return isObject<AgentInfo>(x) && typeof x.imageUrl === 'string';
}
